import type { AxiosRequestConfig } from "axios";
import {
    GatewayService,
    type GoogleDocsAccessResponse,
    type OperationsListParams,
    type OperationsStatsResponse,
    type OperationMetaItem,
    type PaginatedListResponse,
    type SheetPreviewStatsResponse,
} from "@modules/gateway/api/gateway.service";
import type { Client } from "@shared-types";
import type { CsvExportOptions } from "@modules/gateway/api/endpoints";

/**
 * Gateway facade for the YTG module.
 *
 * This facade exposes only the Gateway methods needed by the YTG module,
 * reducing coupling between modules.
 *
 * Goal: instead of importing GatewayService directly in 12+ files, only this
 * file imports GatewayService, and the other YTG files use this facade.
 *
 * Reference: docs/ytg/ACTION_PLAN.md - Task 1.2
 */
export class YtgGatewayFacade {
    private readonly gateway: typeof GatewayService;

    constructor(gatewayService: typeof GatewayService) {
        this.gateway = gatewayService;
    }

    // =========================================================================
    // METADATA - Operation
    // =========================================================================

    /**
     * Fetch the metadata of a YTG operation from the Gateway.
     * Contains: user (id, name, email, picture), enriched client, meta
     * (folder_id, folder_name).
     */
    async getOperationMeta(
        ytgOperationId: number | string,
        options: AxiosRequestConfig = {},
    ): Promise<OperationMetaItem> {
        return this.gateway.getYtgOperationMeta(ytgOperationId, options);
    }

    /** Fetch YTG operations metadata with pagination + server-side filters. */
    async getAllOperationsMeta(
        params: OperationsListParams = {},
    ): Promise<PaginatedListResponse<OperationMetaItem>> {
        return this.gateway.getYtgMetaOperationsAll(params);
    }

    // =========================================================================
    // METADATA - Rows
    // =========================================================================

    /** Rows metadata: { row_id, guide_drive_link, graph_drive_link }. */
    async getRowMetaByOperation(operationId: number | string): Promise<unknown[]> {
        return this.gateway.getYtgRowMetaByOperation(operationId);
    }

    // =========================================================================
    // GOOGLE DRIVE - Folder Management
    // =========================================================================

    /** Associate a Google Drive folder with a YTG operation. */
    async setOperationFolder(
        operationId: number | string,
        folderId: string,
        folderName: string,
    ): Promise<unknown> {
        return this.gateway.setYtgOperationFolder(operationId, folderId, folderName);
    }

    // =========================================================================
    // GOOGLE SHEETS - Import (read from sheet)
    // =========================================================================

    /** Read a Google Sheet's headers for column auto-detection. */
    async getSheetHeaders(
        spreadsheetId: string,
        sheetName: string | null = null,
    ): Promise<unknown> {
        return this.gateway.getSheetHeaders(spreadsheetId, sheetName);
    }

    /** Preview a Google Sheet's parsing stats (without creating the operation). */
    async previewSheet(body: Record<string, unknown>): Promise<SheetPreviewStatsResponse> {
        return this.gateway.previewSheet(body);
    }

    /** Sort Google Docs IDs by whether the app may read them for the current user. */
    async checkGoogleDocsAccess(documentIds: string[]): Promise<GoogleDocsAccessResponse> {
        return this.gateway.checkGoogleDocsAccess(documentIds);
    }

    /** Create a YTG operation from a Google Sheet. */
    async createOperationFromSheet(body: Record<string, unknown>): Promise<unknown> {
        return this.gateway.createOperationFromSheet(body);
    }

    /** Create a YTG operation from pre-mapped rows (client-side parsed CSV). */
    async createOperationFromRows(body: Record<string, unknown>): Promise<unknown> {
        return this.gateway.createOperationFromRows(body);
    }

    // =========================================================================
    // GOOGLE SHEETS - Export
    // =========================================================================

    /**
     * Queue the asynchronous export of an operation's rows to a Google Sheet.
     * The sheet is created in the connected user's Drive by a Celery worker;
     * completion arrives over WebSocket (`sheet.export_completed`).
     */
    async exportOperationToSheet(
        operationId: number | string,
        options: CsvExportOptions = {},
    ): Promise<{ task_id: string; status: string }> {
        return this.gateway.exportYtgOperationToSheet(operationId, options);
    }

    // =========================================================================
    // DOWNLOADS - URLs
    // =========================================================================

    /** Build the download URL for an operation's enriched CSV. */
    downloadOperationEnrichedCsvUrl(
        operationId: number | string,
        options: CsvExportOptions = {},
    ): string {
        return this.gateway.downloadYtgOperationEnrichedCsvUrl(operationId, options);
    }

    // =========================================================================
    // UTILITIES - Image Proxy
    // =========================================================================

    /** Build a proxied URL for an image (e.g. a user avatar). */
    getProxiedImageUrl(imageUrl: string | null | undefined): string | undefined {
        return this.gateway.getProxiedImageUrl(imageUrl);
    }

    // =========================================================================
    // CLIENT DATA (for future use)
    // =========================================================================

    /**
     * Fetch a client's data by its ID.
     * Note: currently used via gateway/hooks/useClientById, but exposed here
     * for consistency.
     */
    async getClientById(clientId: number): Promise<Client> {
        return this.gateway.getClientById(clientId);
    }

    // =========================================================================
    // DASHBOARD STATS
    // =========================================================================

    /** Aggregated YTG operations stats for the dashboard. */
    async getOperationsStats(
        options: AxiosRequestConfig = {},
    ): Promise<OperationsStatsResponse> {
        return this.gateway.getYtgOperationsStats(options);
    }
}

/**
 * Singleton of the YTG Gateway facade.
 * Use it throughout the YTG module instead of importing GatewayService directly.
 */
export const ytgGateway = new YtgGatewayFacade(GatewayService);
